/* eslint-disable no-console */
const LOG_LEVELS = {
	debug: 0,
	info: 1,
	warning: 2,
	error: 3,
};

let currentLogLevel = LOG_LEVELS.warning; // Changed from 'info' to 'warning' to reduce console noise in development
const MAX_LOGS = 1000;

const getCurrentTimestamp = () => Date.now();

const formatLogMessage = (level, message, timestamp = getCurrentTimestamp()) => {
	const date = new Date(timestamp);
	const ISO_STRING_DATE_TIME_LENGTH = 19;
	const formattedDate = date
		.toISOString()
		.replace('T', ' ')
		.substring(0, ISO_STRING_DATE_TIME_LENGTH);
	const levelText = level.toUpperCase();

	return `[${formattedDate}] [${levelText === 'WARNING' ? 'WARN' : levelText}] ${message}`;
};

const shouldLog = level => {
	switch (level) {
		case 'debug':
			return LOG_LEVELS.debug >= currentLogLevel;
		case 'info':
			return LOG_LEVELS.info >= currentLogLevel;
		case 'warning':
			return LOG_LEVELS.warning >= currentLogLevel;
		case 'error':
			return LOG_LEVELS.error >= currentLogLevel;
		default:
			return false;
	}
};

const persistLog = logEntry => {
	try {
		const existingLogs = getLogs();
		const updatedLogs = [logEntry, ...existingLogs].slice(0, MAX_LOGS);

		localStorage.setItem('app_logs', JSON.stringify(updatedLogs));
	} catch (error) {
		console.warn('Failed to persist log:', error);
	}
};

const createLogEntry = (level, message, data = null, error = null) => {
	const timestamp = getCurrentTimestamp();
	const entry = {
		level,
		message,
		timestamp,
		data,
	};

	if (error) {
		if (error instanceof Error) {
			entry.error = {
				message: error.message,
				stack: error.stack,
			};
		} else {
			entry.error = error;
		}
	}

	return entry;
};

export const logInfo = (message, data) => {
	const entry = createLogEntry('info', message, data);

	persistLog(entry);

	if (shouldLog('info')) {
		const formatted = formatLogMessage('info', message, entry.timestamp);

		if (data) {
			console.info(formatted, data);
		} else {
			console.info(formatted);
		}
	}
};

export const logError = (message, error, data) => {
	const entry = createLogEntry('error', message, data, error);

	persistLog(entry);

	if (shouldLog('error')) {
		const formatted = formatLogMessage('error', message, entry.timestamp);

		if (error && data) {
			console.error(formatted, error, data);
		} else if (error) {
			console.error(formatted, error);
		} else if (data) {
			console.error(formatted, data);
		} else {
			console.error(formatted);
		}
	}
};

export const logWarning = (message, data) => {
	const entry = createLogEntry('warning', message, data);

	persistLog(entry);

	if (shouldLog('warning')) {
		const formatted = formatLogMessage('warning', message, entry.timestamp);

		if (data) {
			console.warn(formatted, data);
		} else {
			console.warn(formatted);
		}
	}
};

export const logDebug = (message, data) => {
	const entry = createLogEntry('debug', message, data);

	persistLog(entry);

	if (shouldLog('debug')) {
		const formatted = formatLogMessage('debug', message, entry.timestamp);

		if (data) {
			console.debug(formatted, data);
		} else {
			console.debug(formatted);
		}
	}
};

export const clearLogs = () => {
	localStorage.removeItem('app_logs');
};

export const getLogs = (filters = {}) => {
	try {
		const logs = JSON.parse(localStorage.getItem('app_logs') || '[]');

		if (!filters.level && !filters.minLevel && !filters.since && !filters.until) {
			return logs;
		}

		return logs.filter(log => {
			if (filters.level && log.level !== filters.level) {
				return false;
			}

			if (
				filters.minLevel &&
				Object.prototype.hasOwnProperty.call(LOG_LEVELS, log.level) &&
				Object.prototype.hasOwnProperty.call(LOG_LEVELS, filters.minLevel)
			) {
				if (LOG_LEVELS[log.level] < LOG_LEVELS[filters.minLevel]) {
					return false;
				}
			}

			if (filters.since && log.timestamp < filters.since) {
				return false;
			}

			if (filters.until && log.timestamp > filters.until) {
				return false;
			}

			return true;
		});
	} catch (error) {
		console.warn('Failed to retrieve logs:', error);

		return [];
	}
};

export const exportLogs = (filters = {}) => {
	const logs = getLogs(filters);

	if (filters.includeMetadata) {
		return JSON.stringify({
			metadata: {
				exportDate: new Date().toISOString(),
				totalLogs: logs.length,
				version: '1.0',
			},
			logs,
		});
	}

	return JSON.stringify(logs);
};

export const setLogLevel = level => {
	switch (level) {
		case 'debug':
			currentLogLevel = LOG_LEVELS.debug;
			break;
		case 'info':
			currentLogLevel = LOG_LEVELS.info;
			break;
		case 'warning':
			currentLogLevel = LOG_LEVELS.warning;
			break;
		case 'error':
			currentLogLevel = LOG_LEVELS.error;
			break;
		default:
			// Keep current level
			break;
	}
};

export { formatLogMessage };

export const logPerformance = (operation, duration, options = {}) => {
	let finalDuration = 0;

	if (typeof duration === 'number') {
		finalDuration = duration;
	} else if (typeof performance !== 'undefined' && performance.mark) {
		const message = `Performance: ${operation} took ${duration.toFixed(2)}ms`;

		logInfo(message, {
			operation,
			duration: Math.round(duration),
			...options,
		});
	}

	const message = `Performance: ${operation} took ${finalDuration.toFixed(2)}ms`;

	const logData = {
		operation,
		duration: finalDuration,
		...options,
	};

	logInfo(message, logData);
};

export const logUserAction = (action, context = {}) => {
	const message = `User Action: ${action}`;

	const sessionData = {};

	try {
		const sessionInfo = JSON.parse(localStorage.getItem('session_data') || '{}');

		if (sessionInfo.id) {
			sessionData.sessionId = sessionInfo.id;
		}
		if (sessionInfo.userId) {
			sessionData.userId = sessionInfo.userId;
		}
	} catch (error) {
		// Ignore session parsing errors
	}

	logInfo(message, {
		action,
		timestamp: getCurrentTimestamp(),
		...sessionData,
		...context,
	});
};

export const logSystemEvent = (event, severity = 'info', details = {}) => {
	const message = `System Event: ${event}`;

	let logFunction = logInfo;

	if (severity === 'error') {
		logFunction = logError;
	} else if (severity === 'warning') {
		logFunction = logWarning;
	}

	logFunction(message, {
		event,
		severity,
		...details,
	});
};

export const logAndThrowError = (message, error) => {
	logError(message, error);
	throw error;
};
